"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { loadContentDictionary } from "@/content/i18n/dictionaries";
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  isRtlLocale,
  normalizeLocale,
  type Locale,
} from "@/lib/i18n/config";

type LocaleContextValue = {
  locale: Locale;
  setLocale: (next: Locale) => void;
  dictionaryRevision: number;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

const listeners = new Set<() => void>();
let currentLocale: Locale = DEFAULT_LOCALE;
let dictionaryRevision = 0;
let initialized = false;
let storageListenerAttached = false;

function emitChange() {
  for (const listener of listeners) {
    listener();
  }
}

function applyDocumentLocale(next: Locale) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = next;
  document.documentElement.dir = isRtlLocale(next) ? "rtl" : "ltr";
}

function persistLocale(next: Locale) {
  applyDocumentLocale(next);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
  }
}

function queryLocaleOverride(): Locale | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = new URLSearchParams(window.location.search).get("locale");
    return raw ? normalizeLocale(raw) : null;
  } catch {
    return null;
  }
}

function onStorage(event: StorageEvent) {
  if (event.key !== LOCALE_STORAGE_KEY) return;
  const next = normalizeLocale(event.newValue);
  if (next === currentLocale) return;
  currentLocale = next;
  applyDocumentLocale(next);
  emitChange();
}

function initializeBrowserStore(): boolean {
  if (typeof window === "undefined" || initialized) return false;

  const previousLocale = currentLocale;
  const override = queryLocaleOverride();
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  currentLocale =
    override ??
    (stored
      ? normalizeLocale(stored)
      : normalizeLocale(window.navigator.language));

  persistLocale(currentLocale);

  if (!storageListenerAttached) {
    window.addEventListener("storage", onStorage);
    storageListenerAttached = true;
  }

  initialized = true;
  return previousLocale !== currentLocale;
}

function getSnapshot(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  return currentLocale;
}

function getServerSnapshot(): Locale {
  return DEFAULT_LOCALE;
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  if (typeof window !== "undefined") {
    const localeChanged = initializeBrowserStore();
    if (localeChanged) emitChange();
  }

  return () => {
    listeners.delete(listener);
  };
}

function updateLocale(next: Locale) {
  if (typeof window !== "undefined") {
    initializeBrowserStore();
  }
  const normalized = normalizeLocale(next);
  if (normalized === currentLocale) return;
  currentLocale = normalized;
  persistLocale(normalized);
  emitChange();
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const locale = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [revision, setRevision] = useState(dictionaryRevision);

  useEffect(() => {
    let cancelled = false;
    void loadContentDictionary(locale).then(() => {
      if (cancelled) return;
      dictionaryRevision += 1;
      setRevision(dictionaryRevision);
      emitChange();
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    updateLocale(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, dictionaryRevision: revision }),
    [locale, setLocale, revision],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used inside <LocaleProvider>");
  }
  return ctx;
}

/**
 * Force locale for a subtree during SSR/static export and client render.
 * Used by locale-prefixed routes (`/es/...`) so baked HTML is not English-only.
 */
export function LocaleOverride({
  locale,
  children,
}: {
  locale: Locale;
  children: ReactNode;
}) {
  const parent = useLocale();
  const [revision, setRevision] = useState(parent.dictionaryRevision);

  useEffect(() => {
    let cancelled = false;
    void loadContentDictionary(locale).then(() => {
      if (!cancelled) setRevision((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale: parent.setLocale,
      dictionaryRevision: revision,
    }),
    [locale, parent.setLocale, revision],
  );
  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}
